import type { GetServerSideProps, NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import type { ReactElement } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import type { Locale } from '../locales/messages'
import { extractLocaleFromCookie } from '../lib/locale'

type NextPageWithLayout = NextPage & { getLayout?: (page: ReactElement) => ReactElement }

type Stage = 'collect' | 'verify'

type FormState = {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
}

const planOptions: Array<{ id: string; name: string; price: string; blurb: string; features: string[]; popular?: boolean }> = [
  {
    id: 'basic',
    name: 'Basic',
    price: '€5.99 / month',
    blurb: 'For individuals modernising their study flow with AI.',
    features: ['AI flashcards with citations for 5 documents/month', 'Contextual tutor with citations', 'Exports to Anki, Notion & CSV'],
  },
  {
    id: 'scholar',
    name: 'Scholar',
    price: '€8.99 / month',
    blurb: 'For ambitious students and learning creators.',
    features: ['Everything in Basic with 25 documents/month', 'Priority processing + deeper analytics', 'Adaptive flashcards with spaced repetition'],
    popular: true,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '€10.99 / month',
    blurb: 'For teams, cohorts, and learning organisations.',
    features: ['Unlimited documents & collaboration workspaces', 'Mastery alerts and cohort reporting', 'API & LMS integrations for automation'],
  },
]

const trustedByLogos = ['Nova Labs', 'Brightline', 'Quantum U', 'Atlas Academy', 'Skyward Prep']

const planTestimonials = [
  {
    quote:
      'The AI tutor references our materials flawlessly and lets mentors focus on deeper coaching. Trial to launch took less than a week.',
    name: 'Maya Jackson',
    role: 'Head of Instruction, ForwardCode',
  },
  {
    quote:
      'Compliance training used to take us days to prepare. Lernio automates the study kits and analytics highlight exactly who needs support.',
    name: 'Carlos Rivera',
    role: 'Learning Experience Manager, Clario Health',
  },
  {
    quote:
      'Our cohort-based courses finally have a central workspace—flashcards, tutor chat, and shared exports—all in one place. Students love the response speed.',
    name: 'Aisha Bennett',
    role: 'Program Director, Launch Academy',
  },
]

const planFaqs = [
  {
    question: 'When does my subscription start?',
    answer: 'Access begins as soon as Stripe confirms your payment method. Manage or cancel anytime from Billing.',
  },
  {
    question: 'Do I need to enter my card to start?',
    answer: 'Yes. Stripe securely verifies your payment method so there is no interruption when your subscription starts. You are only charged if you keep the plan active.',
  },
  {
    question: 'Can I switch plans later?',
    answer: 'Absolutely. Upgrade or downgrade from Billing and we prorate unused time automatically. Your workspace and data stay intact.',
  },
  {
    question: 'Do you support teams and institutions?',
    answer: 'Premium includes collaboration workspaces, SSO, and onboarding support. Contact us for dedicated procurement or compliance needs.',
  },
]

const Signup: NextPageWithLayout = () => {
  const router = useRouter()
  const [form, setForm] = useState<FormState>({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' })
  const [stage, setStage] = useState<Stage>('collect')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [authed, setAuthed] = useState<boolean | null>(null)

  const stageParam = useMemo(() => (typeof router.query.stage === 'string' ? router.query.stage : undefined), [router.query.stage])
  const planParam = useMemo(() => (typeof router.query.plan === 'string' ? router.query.plan : undefined), [router.query.plan])
  const showPlan = stageParam === 'plan'
  const googleAuthTarget = useMemo(() => {
    const base = '/signup?stage=plan'
    const target = planParam ? `${base}&plan=${planParam}` : base
    return `/api/auth/oauth/google?redirect=${encodeURIComponent(target)}`
  }, [planParam])

  const handleRequestLink = async () => {
    setBusy(true)
    setMessage('')
    try {
      const payload: Record<string, string> = { email: form.email }
      if (form.firstName || form.lastName) payload.name = `${form.firstName} ${form.lastName}`.trim()
      const redirectTarget = planParam ? `/signup?stage=plan&plan=${encodeURIComponent(planParam)}` : '/signup?stage=plan'
      payload.redirect = redirectTarget
      const r = await fetch('/api/auth/magic/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j?.error || 'Unable to send magic code')
      setStage('verify')
      setMessage('Check your email for a sign-in link and 6-character code to complete sign up.')
    } catch (err: any) {
      setMessage(err?.message || 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleVerify = async () => {
    setBusy(true)
    setMessage('')
    try {
      const fullName = `${form.firstName} ${form.lastName}`.trim()
      const payload: Record<string, string> = { email: form.email, code }
      if (fullName) payload.name = fullName
      const r = await fetch('/api/auth/magic/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j?.detail || 'Invalid code')
      await fetch('/api/auth/session', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: j.token }) })
      const target = planParam ? `/signup?stage=plan&plan=${encodeURIComponent(planParam)}` : '/signup?stage=plan'
      window.location.href = target
    } catch (err: any) {
      setMessage(err?.message || 'Verification failed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    const status = typeof router.query.oauth === 'string' ? router.query.oauth : undefined
    if (!status) return
    const msgMap: Record<string, string> = {
      google_error: 'Google sign-in was cancelled. Please try again.',
      state_mismatch: 'We could not verify the login request. Please start the Google sign-in again.',
      token_error: 'We could not complete Google sign-in. Please retry.',
      verification_failed: 'We could not verify your Google account. Please try again or use email.',
    }
    setMessage(msgMap[status] || 'Sign-in attempt could not be completed. Please try again.')
  }, [router.query.oauth])

  useEffect(() => {
    if (!showPlan) return
    let cancelled = false
    setAuthed(null)
    const selectedName = planOptions.find((p) => p.id === planParam)?.name
    setMessage(
      `Select${selectedName ? ` the ${selectedName}` : ''} plan to start your Lernio subscription. Stripe secures your payment method today, and you can manage billing anytime.`,
    )
    ;(async () => {
      try {
        const r = await fetch('/api/auth/whoami', { cache: 'no-store' })
        if (cancelled) return
        if (r.ok) {
          setAuthed(true)
        } else {
          setAuthed(false)
          setMessage('Please sign in again with Google or email before choosing a plan.')
        }
      } catch {
        if (!cancelled) {
          setAuthed(false)
          setMessage('Please sign in again with Google or email before choosing a plan.')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [showPlan, planParam])

  if (showPlan) {
    const selectedPlanName = planOptions.find((p) => p.id === planParam)?.name
    const infoMessage =
      message ||
      `Select${selectedPlanName ? ` the ${selectedPlanName}` : ''} plan to start your Lernio subscription. Stripe secures your payment method today, and you can manage billing anytime.`
    const infoTone = authed === false ? 'text-rose-600' : 'text-slate-600'

    return (
      <main className="bg-white text-slate-900">
        <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
          <Link href="/" className="flex items-center gap-3" aria-label="Lernio home">
            <Image src="/logo.png" alt="Lernio" width={160} height={48} className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-3 text-sm font-semibold text-cyan-700">
            <span className="hidden text-slate-500 md:inline">Need a demo?</span>
            <a
              href="mailto:hello@lernio.com"
              className="rounded-full border border-cyan-200 px-4 py-2 text-cyan-700 transition hover:border-cyan-300 hover:text-cyan-800"
            >
              Talk to our team
            </a>
          </div>
        </header>

        <section className="mx-auto max-w-3xl px-6 pb-14 pt-6 text-center">
          <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-700">
            Flexible plans • cancel anytime
          </span>
          <h1 className="mt-6 text-4xl font-semibold leading-tight text-slate-900 sm:text-5xl">
            Choose the Lernio plan that fits your learning goals.
          </h1>
          <p className={`mt-4 text-base ${infoTone}`}>{infoMessage}</p>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-16">
          <div className="grid gap-6 lg:grid-cols-3">
            {planOptions.map((plan) => (
              <form
                key={plan.id}
                action="/api/billing/checkout"
                method="POST"
                className={`relative flex h-full flex-col rounded-3xl border p-8 transition ${
                  plan.popular ? 'border-brand bg-white shadow-lg' : 'border-slate-200 bg-white'
                } ${planParam === plan.id ? 'ring-2 ring-brand' : ''}`}
              >
                {plan.popular && (
                  <span className="absolute right-8 top-8 rounded-full bg-brand px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                    Most popular
                  </span>
                )}
                <input type="hidden" name="plan" value={plan.id} />
                <div className="flex flex-col gap-4">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900">{plan.name}</h3>
                    <p className="text-sm text-slate-500">{plan.price}</p>
                  </div>
                  <p className="text-sm text-slate-600">{plan.blurb}</p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={authed === false}
                    className={`inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition ${
                      plan.popular
                        ? 'bg-brand text-white shadow-lg hover:-translate-y-0.5 hover:bg-brand/90'
                        : 'border border-slate-200 text-slate-700 hover:border-cyan-400 hover:text-slate-900'
                    } disabled:pointer-events-none disabled:opacity-60`}
                  >
                    {authed === false ? 'Sign in again to continue' : `Subscribe to ${plan.name}`}
                  </button>
                </div>
              </form>
            ))}
          </div>
          <div className="mt-8 grid gap-4 rounded-3xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <span className="mt-1 h-2 w-2 rounded-full bg-brand" />
              <span>Secure checkout powered by Stripe. Lernio never stores your payment details.</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-1 h-2 w-2 rounded-full bg-brand" />
              <span>Manage or cancel anytime from Billing—automatic renewal reminders included.</span>
            </div>
          </div>
          {authed === false && (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-3xl border border-rose-100 bg-rose-50 p-6 text-sm text-rose-700">
              <p>Your session expired. Sign in again to continue.</p>
              <button
                type="button"
                className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-rose-200 bg-white px-5 py-3 text-sm font-semibold text-rose-700 transition hover:-translate-y-0.5 hover:border-rose-300"
                onClick={() => {
                  window.location.href = googleAuthTarget
                }}
              >
                <GoogleIcon />
                Sign in with Google
              </button>
            </div>
          )}
        </section>

        <section className="bg-slate-50 py-16">
          <div className="mx-auto max-w-6xl px-6 text-center">
            <h3 className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-500">Trusted by learning teams at</h3>
            <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-slate-500">
              {trustedByLogos.map((logo) => (
                <span key={logo} className="rounded-full border border-slate-200 px-4 py-2">
                  {logo}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-6xl px-6 text-center">
            <h3 className="text-3xl font-semibold text-slate-900">Customer love</h3>
            <p className="mt-3 text-base text-slate-600">
              Join thousands of students, educators, and teams who accelerate their learning with Lernio.
            </p>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {planTestimonials.map((testimonial) => (
                <figure key={testimonial.name} className="h-full rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-lg">
                  <blockquote className="text-sm leading-relaxed text-slate-700">“{testimonial.quote}”</blockquote>
                  <figcaption className="mt-4 text-sm font-semibold text-slate-900">
                    {testimonial.name}
                    <span className="mt-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                      {testimonial.role}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-16">
          <div className="mx-auto max-w-4xl px-6 text-center">
            <h3 className="text-3xl font-semibold text-slate-900">Frequently asked questions</h3>
            <div className="mt-8 space-y-4 text-left">
              {planFaqs.map((faq) => (
                <div key={faq.question} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-md">
                  <h4 className="text-base font-semibold text-slate-900">{faq.question}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-4xl px-6 text-center">
            <h3 className="text-3xl font-semibold text-slate-900">Need a tailored rollout?</h3>
            <p className="mt-3 text-base text-slate-600">
              Our team can help map Lernio to your curriculum, compliance requirements, and success metrics. Book a strategy session and we’ll share a customised plan.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <a
                href="mailto:hello@lernio.com"
                className="inline-flex items-center justify-center rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-cyan-400 hover:text-slate-900"
              >
                Email the team
              </a>
              <Link
                href="/pricing"
                className="inline-flex items-center justify-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-brand/90"
              >
                Explore full pricing details
              </Link>
            </div>
          </div>
        </section>
      </main>
    )
  }

  // Minimal single-card signup layout
  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-md text-center">
        <Link href="/" aria-label="Lernio home" className="inline-flex items-center justify-center">
          <Image src="/logo.png" alt="Lernio" width={140} height={40} className="h-9 w-auto" />
        </Link>
      </div>
      <div className="mx-auto mt-6 max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <h2 className="text-left text-2xl font-semibold text-slate-900">Get started</h2>
        <p className="mt-1 text-left text-sm text-slate-600">Create your Lernio account to begin building your Lernio workspace.</p>

        <button
          type="button"
          className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-700 transition hover:border-cyan-400"
          onClick={() => {
            window.location.href = googleAuthTarget
          }}
        >
          <GoogleIcon />
          Continue with Google
        </button>
        <div className="relative my-6 text-center text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
          <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-slate-200" aria-hidden />
          <span className="relative bg-white px-3">Or use your email</span>
        </div>

        {stage === 'collect' ? (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!form.email) { setMessage('Enter a valid email to continue.'); return }; if (form.password && form.password !== form.confirmPassword) { setMessage('Passwords must match.'); return }; void handleRequestLink() }}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">First name</label>
                <input type="text" value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200" placeholder="Ada" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Last name</label>
                <input type="text" value={form.lastName} onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))} className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200" placeholder="Lovelace" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200" placeholder="you@school.edu" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Password (optional)</label>
                <input type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200" placeholder="••••••••" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Confirm password</label>
                <input type="password" value={form.confirmPassword} onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))} className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200" placeholder="••••••••" />
              </div>
            </div>
            <button type="submit" disabled={busy} className="w-full rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-60">
              {busy ? 'Sending magic code…' : 'Create account'}
            </button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!code) { setMessage('Enter the 6-character code you received.'); return }; void handleVerify() }}>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Verification code</label>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm uppercase tracking-[0.3em] shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200" placeholder="ABC123" />
            </div>
            <button type="submit" disabled={busy} className="w-full rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-60">
              {busy ? 'Verifying…' : 'Complete sign up'}
            </button>
            <button type="button" className="w-full rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700" onClick={() => { setStage('collect'); setCode(''); setMessage('') }}>
              Edit details
            </button>
          </form>
        )}

        {message && <p className="mt-4 text-sm text-slate-600">{message}</p>}

        <p className="mt-6 text-xs text-slate-500">
          By creating an account you agree to our <a href="/terms" className="font-semibold text-cyan-600 hover:text-cyan-700">Terms</a> and <a href="/privacy" className="font-semibold text-cyan-600 hover:text-cyan-700">Privacy Policy</a>.
        </p>
        <p className="mt-4 text-sm text-slate-600">
          Already have an account? <Link href="/login" className="font-semibold text-cyan-600 hover:text-cyan-700">Log in</Link>
        </p>
      </div>
    </main>
  )

}

Signup.getLayout = (page) => page

export default Signup

export const getServerSideProps: GetServerSideProps<{ locale?: Locale }> = async ({ req }) => {
  const locale = extractLocaleFromCookie(req.headers?.cookie)
  const token = (req as any).cookies?.token
  if (token && (!req.url || !req.url.includes('stage=plan'))) {
    return { redirect: { destination: '/app', permanent: false } }
  }
  return { props: { locale: locale ?? 'en' } }
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M17.64 9.2045c0-.638-.0573-1.2527-.1636-1.8414H9v3.4818h4.8436c-.2091 1.1277-.8427 2.0845-1.7945 2.7264v2.2687h2.9091c1.7027-1.5682 2.6818-3.8764 2.6818-6.6355z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.4673-.8059 5.9564-2.1605l-2.9091-2.2687c-.8059.54-1.8373.8582-3.0473.8582-2.3441 0-4.3295-1.5845-5.0355-3.7087H.956055v2.3314C2.43727 15.9836 5.48182 18 9 18z" fill="#34A853" />
      <path d="M3.96455 10.7209c-.18-.54-.28364-1.1168-.28364-1.7209 0-.6041.10364-1.1818.28364-1.7218V4.94636H.956364C.347727 6.17045 0 7.54818 0 9c0 1.4518.347727 2.8296.956364 4.0536l3.008186-2.3327z" fill="#FBBC05" />
      <path d="M9 3.57955c1.3209 0 2.5091.45409 3.4427 1.34591l2.5827-2.58273C13.4636.916364 11.4264 0 9 0 5.48182 0 2.43727 2.01636.956055 4.94636l3.008495 2.33182C4.67045 5.16409 6.65591 3.57955 9 3.57955z" fill="#EA4335" />
    </svg>
  )
}
